import React, { useContext, useEffect, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server"
import { useNavigate, generatePath } from "react-router";
import { AppContext } from "../../context/AppContext";
import { DataTable, DataTableColumnsDeepCopy } from "../DataTable";
import { ModuleTypes } from "../ModuleType";

/**
 * PartnerOverviewComponent
 * @constructor
 */
const PartnerOverviewComponent: React.FC = () => {
  const {
    I18N,
    services: {
      servicePartner,
      servicePartnerPartner
    },
    getRoutes
  } = useContext(AppContext);

  /**
   * ROUTE_CONFIG
   */
  const ROUTE_CONFIG = getRoutes();

  /**
   * navigate
   */
  const navigate = useNavigate();

  /**
   * activeTab
   */
  const [activeTab, setActiveTab] = useState("supplier");

  /**
   * suppliers
   */
  const [suppliers, setSuppliers] = useState([]);

  /**
   * customers
   */
  const [customers, setCustomers] = useState([]);

  /**
   * header
   */
  const header = [
    { text: "Customer ID", value: "customer_id" },
    { text: "Supplier ID", value: "supplier_id" },
    { text: "Config", value: "config" },
    { text: "Comment", value: "comment" },
    { text: "Created At", value: "created_at" },
    { text: "Updated At", value: "updated_at" },
    { text: "Customer Contact ID", value: "customer_contact_id" },
    { text: "Supplier Contact ID", value: "supplier_contact_id" },
    { text: "Contact ID", value: "contact_id" },
    { text: "ID", value: "id" },
    { text: "Supplier", value: "supplier" },
    { text: "Customer", value: "customer" }
  ];

  /**
   * columns
   *
   * every VISIBLE column needs renderHeader + renderHeaderReplace, and array
   * index 0 must hold a hidden column: the bundle's tableRender turns each
   * heading text node into a broken {nodeName:"P", data} hybrid that diff-dom
   * can neither match nor replace (the render loop then never converges and
   * freezes the main thread); renderHeaderReplace swaps in a clean node, but
   * tableRender skips the replacement for array index 0 (falsy findIndex), so
   * no visible column may sit there -- the customer tab keeps select 0 visible,
   * so the comment column parks at index 0 for both tabs. select keeps the data
   * mapping intact; hideColumns addresses columns by select, not by position
   */
  const columns = [
    { select: 3, cellClass: "data-comment", headerClass: "data-comment", hidden: true },
    {
      select: 0,
      cellClass: "data-customer-id",
      headerClass: "data-customer-id",
      render: function (_data, _element, _rowIndex, _cellIndex, _row, _headings) {
        let data = _row[11].data || {},
          hashId = data?.hashId,
          // without a hashId there is no logo: an empty src fires a broken
          // image request against the page URL, so the img is skipped entirely
          //@ts-ignore
          logo = hashId ? `<img
            src="${window.PartnerOverviewComponent.servicePartner.getCompanyLogoUrl(hashId)}"
            alt="${data.name} Logo"
            style="max-width:50px; max-height:50px; margin-right:10px;"
          />` : '';

        return `${logo}<span>${data.name}</span>`
      },
      renderHeader: function () {
        // @ts-ignore
        return window.PartnerOverviewComponent.I18N.t("Name")
      },
      renderHeaderReplace: true
    },
    {
      select: 1,
      cellClass: "data-supplier-id",
      headerClass: "data-supplier-id",
      render: function (_data, _element, _rowIndex, _cellIndex, _row, _headings) {
        let data = _row[10].data || {},
          hashId = data?.hashId,
          // without a hashId there is no logo: an empty src fires a broken
          // image request against the page URL, so the img is skipped entirely
          //@ts-ignore
          logo = hashId ? `<img
            src="${window.PartnerOverviewComponent.servicePartner.getCompanyLogoUrl(hashId)}"
            alt="${data.name} Logo"
            style="max-width:50px; max-height:50px; margin-right:10px;"
          />` : '';

        return `${logo}<span>${data.name}</span>`
      },
      renderHeader: function () {
        // @ts-ignore
        return window.PartnerOverviewComponent.I18N.t("Name")
      },
      renderHeaderReplace: true
    },
    {
      select: 2,
      cellClass: "data-config",
      headerClass: "data-config",
      type: "object",
      render: function (_data) {
        // @ts-ignore
        return window.PartnerOverviewComponent.handleModuleTypes(_data);
      },
      renderHeader: function () {
        // @ts-ignore
        return window.PartnerOverviewComponent.I18N.t("Module")
      },
      renderHeaderReplace: true
    },
    { select: 4, cellClass: "data-created-at", headerClass: "data-created-at", hidden: true },
    { select: 5, cellClass: "data-updated-at", headerClass: "data-updated-at", hidden: true },
    { select: 6, cellClass: "data-customer-contact-id", headerClass: "data-customer-contact-id", hidden: true },
    { select: 7, cellClass: "data-supplier-contact-id", headerClass: "data-supplier-contact-id", hidden: true },
    { select: 8, cellClass: "data-contact-id", headerClass: "data-contact-id", hidden: true },
    { select: 9, cellClass: "data-id", headerClass: "data-id", hidden: true },
    {
      select: 10,
      cellClass: "data-supplier",
      headerClass: "data-supplier",
      type: "object",
      render: function (_data, _element, _rowIndex, _cellIndex, _row, _headings) {
        let data = _row[9].data || {},
          id = data?.[0]?.data;

        return `<button
            data-small
            data-primary-dark
            data-icon-only
            title="${window.PartnerOverviewComponent.I18N.t("Partner anzeigen")}"
            aria-label="${window.PartnerOverviewComponent.I18N.t("Partner anzeigen")}"
            onClick="window.PartnerOverviewComponent.handleView('${id}', false)"
          >
            <i data-icon-before="eye"></i>
          </button>`
      },
      renderHeader: function () {
        // @ts-ignore
        return window.PartnerOverviewComponent.I18N.t("Aktion")
      },
      renderHeaderReplace: true
    },
    {
      select: 11,
      cellClass: "data-customer",
      headerClass: "data-customer",
      type: "object",
      render: function (_data, _element, _rowIndex, _cellIndex, _row, _headings) {
        let data = _row[9].data || {},
          id = data?.[0]?.data;

        return `<button
            data-small
            data-primary-dark
            data-icon-only
            title="${window.PartnerOverviewComponent.I18N.t("Partner anzeigen")}"
            aria-label="${window.PartnerOverviewComponent.I18N.t("Partner anzeigen")}"
            onClick="window.PartnerOverviewComponent.handleView('${id}', true)"
          >
            <i data-icon-before="eye"></i>
          </button>`
      },
      renderHeader: function () {
        // @ts-ignore
        return window.PartnerOverviewComponent.I18N.t("Aktion")
      },
      renderHeaderReplace: true
    }
  ];

  /**
   * hideColumns
   *
   * columns are addressed by their select value, not by their position in the
   * array: the hidden column parked at index 0 shifts every position after it,
   * and a stale index would hide the wrong column
   * @param selectArrayToHide
   * @param _columns
   */
  const hideColumns = (
    selectArrayToHide: any = [],
    _columns: any
  ) => {
    // @ts-ignore
    _columns = DataTableColumnsDeepCopy(_columns);

    selectArrayToHide.forEach(
      select => {
        const column = _columns.find(_column => _column.select === select);

        if (column) {
          column['hidden'] = true;
        }
      }
    );

    return _columns;
  }

  /**
   * columnsCustomer -- select values of the supplier-only columns
   */
  const columnsCustomer= hideColumns([1, 7, 10], columns);
  /**
   * columnsSupplier -- select values of the customer-only columns
   */
  const columnsSupplier= hideColumns([0, 6, 11], columns);

  /**
   * options
   */
  const options = {
    searchable: true,
    sortable: false,
    paging: true,
    perPage: 10
  };

  /**
   * load suppliers and customers
   */
  useEffect(() => {
    const loadSuppliersAndCustomers = async () => {
      // the datatable endpoints answer with {data: [...]}: storing the whole
      // response object left suppliers.length forever undefined and the tabs
      // never appeared
      try {
        const suppliers = await servicePartnerPartner.getDatatableSuppliers(),
          supplierRows = suppliers?.data || [];
        setSuppliers(supplierRows);

        const customers = await servicePartnerPartner.getDatatableCustomers(),
          customerRows = customers?.data || [];
        setCustomers(customerRows);

        // the default tab is "supplier"; without suppliers that tab never
        // renders and no tab would match the shown table -- switch to the
        // customers tab, the only one that exists in that case
        if (!supplierRows.length && customerRows.length) {
          setActiveTab("custom");
        }
      } catch (error) {
        console.error('Error fetching partner data:', error);
      }
    };

    loadSuppliersAndCustomers();
  }, [servicePartnerPartner]);

  /**
   * handleView
   * @param id
   * @param isCustom
   */
  const handleView = (id: string, isCustom: boolean) => {
    navigate(generatePath(
      ROUTE_CONFIG.PARTNER_VIEW_EDIT.path, {
        id: id,
        type: isCustom ? "customer" : "supplier"
      }
    ))
  };

  /**
   * handleModuleTypes
   * @param config
   */
  const handleModuleTypes = (config: any) => {
    // @ts-ignore
    return renderToStaticMarkup(<ModuleTypes config={ config } I18N={ window.PartnerOverviewComponent.I18N } showAll />);
  };

  /**
   * static variables
   */
  // @ts-ignore
  window.PartnerOverviewComponent = {
    servicePartner: servicePartner,
    handleView: handleView,
    handleModuleTypes: handleModuleTypes,
    I18N: I18N
  };

  /**
   * renderDataTable
   * @param isCustom
   */
  const renderDataTable = (isCustom: boolean) => (
    <>
      { isCustom ?
        <DataTable
          key="customerDataTable"
          tableId="customerDataTable"
          header={ header }
          options={ options }
          columns={ columnsCustomer }
          server-side="true"
          checkboxes={ false }
          customService={ servicePartnerPartner }
          customServiceName={ "getDatatableCustomers" }
        />
        :
        <DataTable
          key="supplierDataTable"
          tableId="supplierDataTable"
          header={ header }
          options={ options }
          columns={ columnsSupplier }
          server-side="true"
          checkboxes={ false }
          customService={ servicePartnerPartner }
          customServiceName={ "getDatatableSuppliers" }
        />
      }
    </>
  );

  return (
    <>
      {/* Tabs for Suppliers and Customers */}
      <dia-tabs>
        { suppliers.length > 0 && (
          <>
            <input
              type="radio"
              name="tabset"
              id="dia-tab1"
              checked={ activeTab === "supplier" }
              onChange={() => setActiveTab("supplier")}
            />
            <label htmlFor="dia-tab1">{ I18N.t("Lieferanten") }</label>
          </>
        ) }

        { customers.length > 0 && (
          <>
            <input
              type="radio"
              name="tabset"
              id="dia-tab2"
              checked={ activeTab === "custom" }
              onChange={() => setActiveTab("custom")}
            />
            <label htmlFor="dia-tab2">{ I18N.t("Kunden") }</label>
          </>
        ) }
      </dia-tabs>

      { renderDataTable(activeTab === "custom") }
    </>
  );
};

export default PartnerOverviewComponent;
